import React, { useEffect, useState } from 'react';
import BlankTemplate from 'components/Templates/BlankTemplate';
import DemoteEmployee from 'components/Organisms/Employee/EmployeeRecord/ActionList/DemoteEmployee';
import { useThunk } from 'hooks/useThunk';
import { fetchDemoteEmployee } from 'store';
import { Typography, Box, Skeleton } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { demoteEmployeeAction } from 'api/employees';
import dayjs from 'dayjs';
import useToast from 'components/Provider/useToast';

const DemoteEmployeePage = () => {
  const addToast = useToast();
  const navigate = useNavigate();
  const { userId } = useParams();

  const [loading, setLoading] = useState(false);
  const [getDemoteEmployee, isLoadingDemoteEmployee, loadingDemoteEmployeeError] = useThunk(fetchDemoteEmployee);

  useEffect(() => {
    const query = new URLSearchParams({});
    query.append('id', userId);
    getDemoteEmployee(query);
  }, [userId, getDemoteEmployee]);

  const { demoteEmployee } = useSelector((state) => state.employees);

  const employeeData = {
    supervisor: demoteEmployee?.employeeInstance?.supervisor?.fullName,
    department: demoteEmployee?.employeeInstance?.department?.name,
    designation: demoteEmployee?.employeeInstance?.designation?.name,
    location: demoteEmployee?.employeeInstance?.location?.name,
  };

  const supervisorOptions = demoteEmployee?.employeeList;
  const departmentOptions = demoteEmployee?.departmentsList;
  const designationOptions = demoteEmployee?.designationsList;
  const locationOptions = demoteEmployee?.locationsList;
  const userData = demoteEmployee?.user;

  const handleSubmit = async ({ effectiveStartDate, supervisor, department, designation, location, notes }) => {
    const newStartDate = effectiveStartDate ? dayjs(effectiveStartDate).format('MM/DD/YYYY') : '';
    const newNotes = notes || '';
    const bodyFormData = new FormData();
    bodyFormData.append('empID', demoteEmployee?.user?.employeeId);
    bodyFormData.append('CurrentAction', 'SaveAndSubmit');
    bodyFormData.append('effectiveStartDate', newStartDate);
    bodyFormData.append('employee.supervisor.id', supervisor);
    bodyFormData.append('employee.department.id', department);
    bodyFormData.append('employee.designation.id', designation);
    bodyFormData.append('employee.location.id', location);
    bodyFormData.append('notes', newNotes);

    try {
      setLoading(true);
      const response = await demoteEmployeeAction(bodyFormData);
      if (response?.data?.status === 'SUCCESS') {
        addToast({ type: 'success', title: 'SUCCESS', message: response?.data?.message });
        navigate(`/company/employee/view/${userId}`);
      } else {
        addToast({ type: 'error', title: 'ERROR', message: response?.data?.message });
      }
    } catch (error) {
      addToast({ type: 'error', title: 'ERROR', message: error?.response?.data?.message || error?.message });
    } finally {
      setLoading(false);
    }
  };

  if (loadingDemoteEmployeeError !== null) {
    return (
      <Typography variant="h6">
        {loadingDemoteEmployeeError?.message || `Error while loading current details for employee id : ${userId}.`}
      </Typography>
    );
  }

  const breadcrumbData = [
    { id: 1, name: 'Employee List', path: '/company/employee/employees-list', isActive: false },
    { id: 2, name: 'Employee Records', path: `/company/employee/view/${userId}`, isActive: false },
    { id: 2, name: 'Demote Employee', isActive: true },
  ];

  return isLoadingDemoteEmployee ? (
    <Box sx={{ p: 4 }}>
      <Skeleton height={70} />
      <Skeleton />
      <Skeleton />
    </Box>
  ) : (
    <BlankTemplate
      pageHeading={`Demote Employee: ${get(demoteEmployee, 'employeeInstance.fullName', '')} (${get(
        demoteEmployee,
        'employeeInstance.profile.employeeId',
        'NA',
      )})`}
      breadcrumbData={breadcrumbData}
      padding="3rem"
    >
      <DemoteEmployee
        employeeData={employeeData}
        supervisorOptions={supervisorOptions}
        departmentOptions={departmentOptions}
        designationOptions={designationOptions}
        locationOptions={locationOptions}
        userData={userData}
        handleSubmit={handleSubmit}
        loading={loading}
        btnLabel="Demote"
      />
    </BlankTemplate>
  );
};

export default DemoteEmployeePage;
